import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { SetState } from '@/types/helpers';

interface MarketWidgetContextType {
  isFullScreen: boolean;
  setIsFullScreen: SetState<boolean>;
  setFullScreenAvailable: SetState<boolean>;
  fullScreenAvailable: boolean;
}

const MarketWidgetContext = createContext<MarketWidgetContextType | undefined>(undefined);

interface MarketAppControlsProviderProps {
  children: ReactNode;
}

export const MarketWidgetProvider = ({ children }: MarketAppControlsProviderProps) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullScreenAvailable, setFullScreenAvailable] = useState(false);

  const value = useMemo(
    () => ({
      isFullScreen,
      setIsFullScreen,
      setFullScreenAvailable,
      fullScreenAvailable
    }),
    [fullScreenAvailable, isFullScreen]
  );

  return <MarketWidgetContext.Provider value={value}>{children}</MarketWidgetContext.Provider>;
};

export const useMarketWidget = (): MarketWidgetContextType => {
  const context = useContext(MarketWidgetContext);
  if (context === undefined) {
    throw new Error('useMarketWidget must be used within a MarketWidgetProvider');
  }
  return context;
};

const MARKET_WIDGET_DEFAULTS: MarketWidgetContextType = {
  isFullScreen: false,
  setIsFullScreen: () => {},
  setFullScreenAvailable: () => {},
  fullScreenAvailable: false
};

export const useOptionalMarketWidget = (): MarketWidgetContextType => {
  const context = useContext(MarketWidgetContext);
  return context ?? MARKET_WIDGET_DEFAULTS;
};
